import { useModalStore } from '@/stores/modal'
import { getToastDuration } from '@/config/toastConfig.js'

class ValidationErrorHandlingService {
    constructor(router, toast) {
        this.router = router
        this.toast = toast
        this.modalStore = null

        this.entityRouteMap = {
            client: {
                route: 'ClientShow',
                paramKey: 'id',
                displayName: '§ui.message_client_data_missing',
                editRoute: 'client/edit',
            },
            contract: {
                route: 'ContractUpdate',
                paramKey: 'id',
                displayName: '§ui.message_contract_data_missing',
                editRoute: 'contract/update',
            },
            unit: {
                route: 'UnitShow',
                paramKey: 'id',
                displayName: '§ui.message_unit_data_missing',
                editRoute: 'unit/edit',
            },
        }

        this.currentValidationContext = null
    }

    handleValidationError(error) {
        try {
            const response = error.response
            const errors = response?.data?.errors || []
            const message = response?.data?.message || 'Validation failed'

            if (!errors || errors.length === 0) {
                this.showGenericValidationToast(message)
                return
            }

            const currentRoute = this.router.currentRoute.value
            const entitiesInError = this.extractEntitiesFromErrors(errors)
            const currentEntity = this.getCurrentEntityFromRoute(currentRoute)

            if (
                !entitiesInError.length ||
                this.isSameEntityValidation(entitiesInError, currentEntity)
            ) {
                this.handleSameEntityValidation(message, errors)
            } else {
                this.handleCrossEntityValidation(errors, message, currentRoute)
            }
        } catch (handlingError) {
            console.error('Error handling validation error:', handlingError)
            this.showGenericValidationToast('Validation error occurred')
        }
    }

    extractEntitiesFromErrors(errors) {
        const entities = new Set()

        Object.keys(errors).forEach((key) => {
            const error = errors[key]
            if (error.entity) {
                entities.add(error.entity)
            }
        })

        return Array.from(entities)
    }

    getCurrentEntityFromRoute(route) {
        const routePath = route.path

        const routeEntityMap = {
            'client/show': 'client',
            'client/edit': 'client',
            'contract/show': 'contract',
            'contract/edit': 'contract',
            'unit/show': 'unit',
            'unit/edit': 'unit',
        }

        const routeKey = Object.keys(routeEntityMap).find((key) => routePath.includes(key))
        return routeKey ? routeEntityMap[routeKey] : null
    }

    isSameEntityValidation(entitiesInError, currentEntity) {
        if (!currentEntity || entitiesInError.length === 0) {
            return false
        }

        return entitiesInError.length === 1 && entitiesInError[0] === currentEntity
    }

    handleSameEntityValidation(message, errors) {
        this.showToastMessage(message, 'error')

        this.emitSameEntityValidationEvent(errors)
    }

    handleCrossEntityValidation(errors, message, sourceRoute) {
        this.modalStore = useModalStore()
        const groupedErrors = this.groupErrorsByEntity(errors)

        this.currentValidationContext = {
            errors: groupedErrors,
            message: message,
            sourceRoute: sourceRoute,
            timestamp: Date.now(),
        }

        if (this.modalStore) {
            this.showValidationModal(groupedErrors, message)
        } else {
            this.showCrossEntityToast(groupedErrors, message)
        }
    }

    groupErrorsByEntity(errors) {
        const grouped = {}

        Object.keys(errors).forEach((field) => {
            const error = errors[field]
            const entity = error.entity || 'unknown'

            if (!grouped[entity]) {
                grouped[entity] = {
                    entityType: entity,
                    entityId: error.id,
                    errors: [],
                    navigationInfo: this.entityRouteMap[entity] || null,
                }
            }

            grouped[entity].errors.push({
                field: field,
                message: error.message,
            })
        })

        return grouped
    }

    showValidationModal(groupedErrors, message) {
        this.modalStore.setOpen({
            name: 'ValidationErrorsModal',
            size: 'medium',
            title: '§ui.message_validation_errors_title',
            data: {
                groupedErrors: groupedErrors,
                entityRouteMap: this.entityRouteMap,
            },
        })
    }

    handleEntityNavigation(entityType, entityId, errors = null) {
        const routeConfig = this.entityRouteMap[entityType]

        if (!routeConfig) {
            console.error(`No route configuration for entity type: ${entityType}`)
            return
        }

        const routeParams = {
            [routeConfig.paramKey]: entityId,
        }

        const queryParams = {
            validationContext: 'true',
            returnTo: this.router.currentRoute.value.fullPath,
        }

        // Include field-level errors in query params if provided
        if (errors && errors.length > 0) {
            queryParams.validationErrors = encodeURIComponent(JSON.stringify(errors))
        }

        const routeOptions = {
            name: routeConfig.editRoute || routeConfig.route,
            params: routeParams,
            query: queryParams,
        }

        if (this.modalStore && this.modalStore.isOpen) {
            this.modalStore.setClose()
        }

        this.router.push(routeOptions)
        this.showToastMessage(
            `Navigate to ${routeConfig.displayName} to complete required information`,
            'info'
        )
    }

    showCrossEntityToast(groupedErrors, message) {
        const entityNames = Object.values(groupedErrors)
            .map((group) => group.entityName)
            .join(', ')

        this.showToastMessage(`${message}. Please complete: ${entityNames}`, 'warn', 5000)
    }

    showToastMessage(message, severity = 'warn', life = null) {
        if (this.toast && this.toast.add) {
            this.toast.add({
                severity: severity,
                detail: message,
                life: life || getToastDuration(message, severity),
            })
        }
    }

    showGenericValidationToast(message) {
        this.showToastMessage(message, 'warn')
    }

    emitSameEntityValidationEvent(errors) {
        if (typeof window !== 'undefined' && window.dispatchEvent) {
            const event = new CustomEvent('validation-errors', {
                detail: { errors },
            })
            window.dispatchEvent(event)
        }
    }

    clearValidationContext() {
        this.currentValidationContext = null
    }

    getValidationContext() {
        return this.currentValidationContext
    }

    addEntityRouteMapping(entityType, routeConfig) {
        this.entityRouteMap[entityType] = {
            route: routeConfig.route,
            paramKey: routeConfig.paramKey || 'id',
            displayName: routeConfig.displayName || entityType,
            editRoute: routeConfig.editRoute || routeConfig.route,
        }
    }

    hasEntityRouteMapping(entityType) {
        return !!this.entityRouteMap[entityType]
    }
}

export default ValidationErrorHandlingService
